/**
 * MH-14 Trình phê duyệt (mục 5.4 đặc tả giao diện) — phần danh sách, dựng lại
 * theo trang 'trinh-duyet' của bản mẫu CMS: hàng lọc Tên bản tin + Loại file,
 * bảng như danh sách nội dung kèm cột Người tạo, thao tác Duyệt / Từ chối.
 *
 * GIỮ QUY TẮC BẮT BUỘC NGHE TRƯỚC KHI DUYỆT: nút "Duyệt" và "Từ chối" ở đây
 * không gọi API duyệt trực tiếp mà dẫn vào MH-14 chi tiết (`MH14DuyetBanTin`),
 * nơi nút duyệt chỉ bật sau khi nghe hết hoặc xác nhận đã nghe, và "từ chối"
 * bắt buộc ghi lý do. Cờ "đã nghe" (`daNghe`) do máy chủ trả kèm mỗi dòng.
 *
 * `GET /api/v1/bulletins/pending-approval` nhận `page`, `pageSize`, `nguonId`;
 * ô lọc Loại bản tin (gộp kho 29/10/2026) gửi thêm `loaiBanTin` (Âm thanh `1,3`
 * / Hình ảnh `2` / Video `4`) — A1 cần nhận tham số này ở hàng đợi duyệt. Ô tìm
 * tên vẫn khoá, gắn "Chưa có API"; dòng dữ liệu không có tên người tạo → cột
 * "Người tạo" hiện gạch ngang.
 */
import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung as t } from '../ngon-ngu/vi-bien-tap';
import { banTinApi, type DongBanTin } from '../api/ban-tin-api';
import type { TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag } from '../thanh-phan/MauGiaoDien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BieuTuongNoiDung } from './ChonLoaiNoiDung';
import {
  nhanNhomLoai,
  OChonLoaiBanTin,
  OLoc,
  TagTrangThaiBanTin,
  TagUuTien,
  TenBanTinCoBieuTuong,
  thoiGianNgan,
} from './MH11DanhSachBanTin';
import './bien-tap.css';

type DongHangDoi = DongBanTin & { daNghe: boolean };

export interface MH14ListProps {
  api?: Pick<typeof banTinApi, 'hangDoiChoDuyet'>;
}

export function MH14HangDoiDuyet({ api }: MH14ListProps = {}): ReactNode {
  const goi = api ?? banTinApi;
  const phien = dungPhien();
  const [thamSo, datThamSo] = useSearchParams();
  const trang = Number(thamSo.get('page') ?? 1) || 1;
  const soDong = Number(thamSo.get('pageSize') ?? SO_DONG_MAC_DINH) || SO_DONG_MAC_DINH;
  const nguonId = phien.nguonIdHienTai;
  const loaiBanTin = thamSo.get('loaiBanTin') ?? '';

  const kq = dungTaiDuLieu<TrangDuLieu<DongHangDoi>>({
    tai: (signal) =>
      goi.hangDoiChoDuyet(
        { page: trang, pageSize: soDong, nguonId, loaiBanTin: loaiBanTin || undefined },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: loaiBanTin !== '',
    phuThuoc: [trang, soDong, nguonId, loaiBanTin],
  });

  const datLocLoai = (v: string): void => {
    const moi = new URLSearchParams(thamSo);
    if (v) moi.set('loaiBanTin', v);
    else moi.delete('loaiBanTin');
    moi.set('page', '1');
    datThamSo(moi, { replace: true });
  };

  const dsDong = useMemo(() => kq.duLieu?.items ?? [], [kq.duLieu]);

  const cot: CotBang<DongHangDoi>[] = useMemo(
    () => [
      {
        khoa: 'stt',
        nhan: t.danhSach.cot.stt,
        ve: (d) => <span className="nd-stt">{(trang - 1) * soDong + dsDong.indexOf(d) + 1}</span>,
      },
      {
        khoa: 'tieuDe',
        nhan: t.danhSach.cot.tenBanTin,
        ve: (d) => <TenBanTinCoBieuTuong d={d} lienKet={`/ban-tin/duyet/${d.id}`} />,
      },
      {
        khoa: 'loaiBanTin',
        nhan: t.danhSach.cot.loaiBanTin,
        ve: (d) => <span data-loai-ban-tin={d.loaiBanTin}>{nhanNhomLoai(d.loaiBanTin)}</span>,
      },
      {
        khoa: 'diaBan',
        nhan: t.danhSach.cot.diaBan,
        ve: (d) => <span>{d.tenDiaBanTao ?? d.diaBanTao}</span>,
      },
      {
        khoa: 'thoiLuong',
        nhan: t.danhSach.cot.thoiLuong,
        ve: (d) => <span>{d.thoiLuong ?? vi.banTin.chuaCo}</span>,
      },
      {
        khoa: 'trangThai',
        nhan: t.danhSach.cot.trangThai,
        ve: (d) => (
          <span className="nd-thao-tac">
            <TagTrangThaiBanTin trangThai={d.trangThai} />
            <span data-da-nghe={d.daNghe}>
              <TheTag kieu={d.daNghe ? 'xanh' : 'trung-tinh'}>
                {d.daNghe ? vi.duyet.daNghe : t.hangDoi.chuaNghe}
              </TheTag>
            </span>
          </span>
        ),
      },
      { khoa: 'mucDoUuTien', nhan: t.danhSach.cot.mucDoUuTien, ve: (d) => <TagUuTien muc={d.mucDoUuTien} /> },
      {
        khoa: 'capNhat',
        nhan: t.danhSach.cot.thoiGianCapNhat,
        ve: (d) => <span className="nd-nw">{thoiGianNgan(d.updatedAt)}</span>,
      },
      {
        khoa: 'nguoiTao',
        nhan: t.danhSach.cot.nguoiTao,
        ve: () => <span title={t.danhSach.nguoiTaoChuaCo}>{vi.banTin.chuaCo}</span>,
      },
      {
        khoa: 'hanhDong',
        nhan: t.danhSach.cot.thaoTac,
        ve: (d) => (
          <div className="nd-thao-tac bang__hanh-dong">
            <Link
              to={`/ban-tin/duyet/${d.id}`}
              className="nut nut--nho nd-nut-duyet"
              title={t.hangDoi.duyetGoiY}
              data-hanh-dong="duyet"
            >
              <BieuTuongNoiDung ten="tich" co={14} net={2} />
              {t.hangDoi.duyet}
            </Link>
            <Link
              to={`/ban-tin/duyet/${d.id}`}
              className="nut nut--nho nd-nut-tu-choi"
              title={t.hangDoi.tuChoiGoiY}
              data-hanh-dong="tu-choi"
            >
              {t.hangDoi.tuChoi}
            </Link>
          </div>
        ),
      },
    ],
    [trang, soDong, dsDong],
  );

  return (
    <div className="man-hinh" data-man-hinh="MH-14">
      <h1 className="chi-cho-doc-man-hinh">{vi.menuCon.trinhPheDuyet}</h1>
      <div className="thanh-loc-mau nd-loc" role="search">
        <OLoc nhan={t.danhSach.locTen} htmlFor="hd-tim" chuaCoApi>
          <input id="hd-tim" type="search" disabled placeholder={t.danhSach.locTenGoiY} />
        </OLoc>
        <OChonLoaiBanTin id="hd-loai-file" giaTri={loaiBanTin} onDoi={datLocLoai} />
      </div>
      <p className="bien-tap__ghi-chu">{vi.duyet.dieuKienDuyet}</p>
      <div className="nd-bang-viet-hoa">
        <BangDuLieu<DongHangDoi>
          nhan={vi.menuCon.trinhPheDuyet}
          cot={cot}
          dong={dsDong}
          khoaDong={(d) => d.id}
          nhanDong={(d) => d.tieuDe}
          trangThai={kq.trangThai}
          loi={kq.loi}
          taiLau={kq.taiLau}
          onThuLai={kq.taiLai}
          tong={kq.duLieu?.total ?? 0}
          trang={trang}
          soDongMoiTrang={soDong}
          onDoiTrang={(so) => {
            const moi = new URLSearchParams(thamSo);
            moi.set('page', String(so));
            datThamSo(moi, { replace: true });
          }}
          onDoiSoDong={(s) => {
            const moi = new URLSearchParams(thamSo);
            moi.set('pageSize', String(s));
            moi.set('page', '1');
            datThamSo(moi, { replace: true });
          }}
          dangLoc={loaiBanTin !== ''}
          onXoaLoc={() => datLocLoai('')}
          moTaRong={vi.duyet.rongMoTa}
        />
      </div>
    </div>
  );
}
